import { useState, FormEvent } from "react";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import SeoMeta from "../../components/base/SeoMeta";

const BASE_URL = (import.meta.env.VITE_SITE_URL as string) || "https://flightfare.pk";

const CONTACT_JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact flightfare.pk – Travel Agency Lahore",
    "url": `${BASE_URL}/contact`,
    "description": "Contact flightfare.pk travel agency in Bahria Town, Lahore for flight bookings, tour packages, Umrah, visa assistance and travel insurance."
  },
  {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "name": "flightfare.pk",
    "alternateName": "Quick Fly Travel",
    "url": BASE_URL,
    "telephone": "+923711432451",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "GF & BM 9A, AA Commercial, Street 1, Sector D",
      "addressLocality": "Bahria Town, Lahore",
      "addressRegion": "Punjab",
      "addressCountry": "PK"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 31.3585,
      "longitude": 74.1755
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+923711432451",
        "contactType": "customer service",
        "areaServed": "PK",
        "availableLanguage": ["English", "Urdu"]
      },
      {
        "@type": "ContactPoint",
        "telephone": "+923711432451",
        "contactType": "sales",
        "contactOption": "TollFree",
        "areaServed": "PK"
      }
    ],
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
        "opens": "09:00",
        "closes": "20:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Sunday",
        "opens": "11:00",
        "closes": "17:00"
      }
    ]
  }
];

const FORM_URL = "https://readdy.ai/api/form/d726f4rk1jkj467r5srg";

const CONTACT_INFO = [
  {
    icon: "ri-map-pin-2-line",
    label: "Office Address",
    lines: ["GF & BM 9A, AA Commercial,", "Street 1, Sector D,", "Bahria Town, Lahore"],
    color: "text-cyan-600",
    bg: "bg-cyan-50",
  },
  {
    icon: "ri-phone-line",
    label: "Phone",
    lines: ["03711432451"],
    href: "tel:+923711432451",
    color: "text-amber-500",
    bg: "bg-amber-50",
  },
  {
    icon: "ri-whatsapp-line",
    label: "WhatsApp",
    lines: ["+92 371 1432451"],
    href: "https://wa.me/923711432451?text=I+have+a+query+about+flightfare.pk",
    color: "text-green-600",
    bg: "bg-green-50",
  },
  {
    icon: "ri-time-line",
    label: "Office Hours",
    lines: ["Mon – Sat: 9:00 AM – 8:00 PM", "Sunday: 11:00 AM – 5:00 PM"],
    color: "text-violet-600",
    bg: "bg-violet-50",
  },
];

export default function ContactPage() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (!phone.trim()) { setError("Please enter your phone number."); return; }
    if (!subject.trim()) { setError("Please enter a subject."); return; }
    if (!message.trim()) { setError("Please enter your message."); return; }
    if (message.length > 500) { setError("Message must be 500 characters or fewer."); return; }
    setError("");
    setLoading(true);
    try {
      const body = new URLSearchParams({
        "Full Name": name,
        "Phone": `+92${phone}`,
        "Email": email || "Not provided",
        "Subject": subject,
        "Message": message,
      });
      await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SeoMeta
        title="Contact flightfare.pk – Travel Agency Bahria Town Lahore Pakistan"
        description="Get in touch with flightfare.pk travel agency in Bahria Town, Lahore. Call 03711432451 for cheap flights, tour packages, Umrah, visa and insurance queries. Open Mon-Sat 9AM-8PM."
        keywords="contact flightfare.pk, travel agency Lahore contact, Bahria Town travel agency, Pakistan travel agency phone, flight booking inquiry Lahore"
        canonical="/contact"
        jsonLd={CONTACT_JSON_LD}
      />

      {/* ── Hero ── */}
      <div className="pt-16">
        <div className="relative w-full h-56 md:h-72 overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/993fdebea54402772aba12014eb0257b.jpg"
            alt="Contact flightfare.pk"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/15" />
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-amber-400" />
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-12 pb-8">
            <div className="max-w-7xl mx-auto">
              <p className="text-amber-300 text-xs font-bold tracking-widest uppercase mb-2">
                <a href="/" className="hover:underline cursor-pointer">Home</a>
                <span className="mx-2 text-white/40">/</span>
                Contact Us
              </p>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight">Get in Touch</h1>
              <p className="text-white/80 text-sm mt-1.5 max-w-lg">
                We&apos;re here to help — whether it&apos;s a flight query, tour booking, Umrah package, or visa assistance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cyan Info Bar ── */}
      <div className="bg-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 grid grid-cols-1 md:grid-cols-3 gap-2 md:divide-x divide-white/20">
          {[
            { icon: "ri-phone-fill", label: "Call Us", value: "03711432451", href: "tel:+923711432451" },
            { icon: "ri-whatsapp-fill", label: "WhatsApp", value: "+92 371 1432451", href: "https://wa.me/923711432451" },
            { icon: "ri-map-pin-fill", label: "Visit Us", value: "Bahria Town, Lahore" },
          ].map(item => (
            <div key={item.label} className="flex items-center justify-center gap-3 px-4 py-1">
              <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-xl text-white/80`}></i>
              </div>
              <div>
                <p className="text-white/70 text-xs">{item.label}</p>
                {item.href ? (
                  <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                    className="text-white font-bold text-sm hover:text-amber-300 transition-colors cursor-pointer whitespace-nowrap">
                    {item.value}
                  </a>
                ) : (
                  <p className="text-white font-bold text-sm whitespace-nowrap">{item.value}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">

          {/* ── Left: Contact Info ── */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div>
              <p className="text-cyan-600 text-xs font-bold tracking-widest uppercase mb-1">Contact Details</p>
              <h2 className="text-xl font-extrabold text-gray-900">We&apos;d Love to Hear From You</h2>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                Drop by our office in Bahria Town, Lahore, give us a call, or fill in the form and we&apos;ll get back to you within a few hours.
              </p>
            </div>

            {CONTACT_INFO.map(info => (
              <div key={info.label} className="flex items-start gap-4 bg-gray-50 rounded-xl border border-gray-100 p-4">
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${info.bg} flex-shrink-0`}>
                  <i className={`${info.icon} text-xl ${info.color}`}></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{info.label}</p>
                  {info.href ? (
                    <a href={info.href} target={info.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                      className={`text-sm font-semibold ${info.color} hover:underline cursor-pointer`}>
                      {info.lines[0]}
                    </a>
                  ) : (
                    info.lines.map((line, i) => (
                      <p key={i} className="text-sm text-gray-700 leading-snug">{line}</p>
                    ))
                  )}
                </div>
              </div>
            ))}

            {/* Social Links */}
            <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Follow Us</p>
              <div className="flex items-center gap-3">
                <a href="https://www.facebook.com/share/1E1YkhMex1/" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="http://instagram.com/flightfare.pk/" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-pink-50 text-pink-500 hover:bg-pink-100 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
                <a href="https://wa.me/923711432451" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-colors cursor-pointer">
                  <i className="ri-whatsapp-fill text-lg"></i>
                </a>
              </div>
            </div>
          </div>

          {/* ── Right: Form ── */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-cyan-600 to-cyan-500 px-6 py-5">
                <h3 className="text-white font-bold text-base">Send Us a Message</h3>
                <p className="text-cyan-100 text-xs mt-0.5">We typically respond within a few hours during office hours.</p>
              </div>

              {submitted ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-5">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h4 className="text-lg font-extrabold text-gray-900 mb-2">Message Received!</h4>
                  <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">
                    Thanks for reaching out. Our team will get back to you shortly — usually within a few hours.
                  </p>
                  <button type="button"
                    onClick={() => { setSubmitted(false); setName(""); setPhone(""); setEmail(""); setSubject(""); setMessage(""); }}
                    className="text-sm text-cyan-600 font-semibold cursor-pointer hover:underline whitespace-nowrap">
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="p-6 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                      <input
                        name="name" value={name} onChange={e => setName(e.target.value)}
                        placeholder="Your full name"
                        className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone No. <span className="text-red-500">*</span></label>
                      <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden focus-within:border-cyan-400 transition-colors">
                        <span className="px-3 py-2.5 bg-gray-50 border-r border-gray-200 text-sm text-gray-500 whitespace-nowrap select-none">+92</span>
                        <input
                          name="phone" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                          placeholder="3001234567" maxLength={10}
                          className="flex-1 px-3 py-2.5 text-sm outline-none bg-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Email <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <input
                      name="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject <span className="text-red-500">*</span></label>
                    <select
                      name="subject" value={subject} onChange={e => setSubject(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer bg-white appearance-none"
                    >
                      <option value="">Select a subject...</option>
                      <option value="Flight Booking">Flight Booking</option>
                      <option value="Tour Package Inquiry">Tour Package Inquiry</option>
                      <option value="Umrah Package">Umrah Package</option>
                      <option value="Visa Assistance">Visa Assistance</option>
                      <option value="Travel Insurance">Travel Insurance</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Complaint or Feedback">Complaint or Feedback</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Message <span className="text-red-500">*</span></label>
                    <textarea
                      name="message" value={message} onChange={e => setMessage(e.target.value)}
                      maxLength={500} rows={5}
                      placeholder="Tell us how we can help you..."
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                    />
                    <p className="text-xs text-gray-400 text-right mt-0.5">{message.length}/500</p>
                  </div>

                  {error && (
                    <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">{error}</p>
                  )}

                  <button
                    type="submit" disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-bold py-3 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-send-plane-fill text-base"></i>
                    {loading ? "Sending..." : "SEND MESSAGE"}
                  </button>

                  <p className="text-xs text-gray-400 text-center">
                    Or call us directly on <a href="tel:+923711432451" className="text-cyan-600 font-semibold hover:underline cursor-pointer">03711432451</a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Google Map ── */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 pb-14">
        <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: "380px" }}>
          <iframe
            title="flightfare.pk Office Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3399.2!2d74.1755!3d31.3585!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391905b328f9b7b9%3A0x7d9b9c4f8e8e8e8e!2sBahria+Town+Lahore%2C+Sector+D!5e0!3m2!1sen!2spk!4v1700000000000!5m2!1sen!2spk"
            width="100%"
            height="380"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
        <div className="mt-4 flex items-start gap-3 bg-gray-50 rounded-xl border border-gray-100 px-5 py-4">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-cyan-50 flex-shrink-0 mt-0.5">
            <i className="ri-map-pin-2-line text-lg text-cyan-600"></i>
          </div>
          <div>
            <p className="text-sm font-extrabold text-gray-900">flightfare.pk — Main Office</p>
            <p className="text-sm text-gray-500 mt-0.5">GF &amp; BM 9A, AA Commercial, Street 1, Sector D, Bahria Town, Lahore</p>
          </div>
          <a
            href="https://maps.google.com/?q=Bahria+Town+Sector+D+Lahore+AA+Commercial"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto flex items-center gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-bold px-4 py-2 rounded-lg cursor-pointer transition-colors whitespace-nowrap flex-shrink-0"
          >
            <i className="ri-external-link-line text-sm"></i>
            Get Directions
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
